import React, { useCallback, useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import { useLocation } from "react-router-dom"
import { Button, Dropdown, Flex, Tabs } from "antd"
import { DownloadOutlined, PlusOutlined } from "@ant-design/icons"
import { useDispatch } from "react-redux"
import useActiveGuide from "hooks/useActiveClient"
import { BreadCrumbs } from "ui/BreadCrumbs"
import { useTranslation } from "react-i18next"
import { api } from "store/helpers/api"
import { cleanExpenses, getGuideAccounting, getGuideAccountingOverview, getGuideTipReport } from "store/accounting"
import { DEFAULT_RANGE, DEFAULT_TYPE, EXPORT_ALL, SCOUP_LIST, TYPE_OPTIONS_ENUM, TYPE_WITH_DATESWITCHER } from "constants/accounting"
import { DATE_FORMAT } from "modules/datepicker/constants"
import dayjs from "dayjs"
import FormAntdModal from "./FormAntdModal"
import styles from "./Header.module.css"

function buildDateQuery(params) {
  if (params.date_gteq && params.date_lteq) {
    return { date_gteq: params.date_gteq, date_lteq: params.date_lteq }
  }
  if (params.date && params.range && params.range !== "custom") {
    let range
    if (params.range === "week") range = "weekDay"
    else if (params.range === "day") range = "date"
    else range = params.range
    const date = global.dateTime(params.date)
    return { date_gteq: date?.startOf(range, 1)?.format(), date_lteq: date?.endOf(range, 1)?.format() }
  }
  return null
}

function buildTipReportParams(params, q) {
  const crewIds = Array.isArray(params["crew_ids[]"]) ? params["crew_ids[]"] : []
  const result = { q, page: params.page || 1, per_page: params.per_page || 10 }
  if (crewIds.length) result["crew_profile_ids[]"] = crewIds
  result.include_unassigned = params.include_unassigned ?? true
  return result
}

function buildSalesParams(searchParams, params, salesPagination = {}) {
  if (params.payment_type) searchParams.payment_type = params.payment_type
  if (params.booking_type) searchParams.booking_type = params.booking_type
  if (params.transaction_type) searchParams.transaction_type = params.transaction_type
  if (params.experience) searchParams.experience = params.experience
  if (params.search) searchParams.search = params.search
  searchParams.page = salesPagination.page || 1
  searchParams.per_page = salesPagination.per_page || 10
  return searchParams
}

function buildExpensesParams(searchParams, params, expensePagination = {}) {
  if (params.category_id) searchParams.category_id = params.category_id
  if (params.label_ids) searchParams.label_ids = params.label_ids
  if (params.search) searchParams.search = params.search
  searchParams.page = expensePagination.page || 1
  searchParams.per_page = expensePagination.per_page || 10
  return searchParams
}

function buildTypeSpecificParams(searchParams, params, salesPagination, expensePagination) {
  if (params.type === "sales") return buildSalesParams(searchParams, params, salesPagination)
  if (params.type === "expenses") return buildExpensesParams(searchParams, params, expensePagination)
  return searchParams
}

export const requestParams = (params, salesPagination, expensePagination) => {
  if (!params?.type) return undefined
  const q = buildDateQuery(params)
  if (!q) return undefined

  if (params.type === "tip_report") return buildTipReportParams(params, q)

  const searchParams = { include_stats: true, q }
  if (SCOUP_LIST?.includes(params.type)) searchParams.scope = params.type.toLowerCase()
  return buildTypeSpecificParams(searchParams, params, salesPagination, expensePagination)
}

function buildExportFilename(downloadType, type) {
  if (downloadType && typeof downloadType === "string") return `${downloadType}_report.csv`
  if (type === "expenses" || type === "sales" || type === "tip_report") return `${type}_report.csv`
  return "report.csv"
}

function buildExportParams(params, downloadType) {
  let exportParams = requestParams(params)
  if (!exportParams) {
    exportParams = {}
    if (params?.date_gteq && params?.date_lteq) {
      exportParams.q = { date_gteq: params.date_gteq, date_lteq: params.date_lteq }
    }
  }
  if (downloadType && typeof downloadType === "string" && params?.type === "business_overview") {
    exportParams.scope = downloadType
  }
  exportParams.per_page = EXPORT_ALL
  return exportParams
}

Header.propTypes = {
  params: PropTypes.shape({
    type: PropTypes.string,
    date: PropTypes.string,
    range: PropTypes.string,
    overview_range: PropTypes.string,
    date_gteq: PropTypes.string,
    date_lteq: PropTypes.string,
    search: PropTypes.string,
    payment_type: PropTypes.string,
    booking_type: PropTypes.string,
    transaction_type: PropTypes.string,
    experience: PropTypes.string,
    category_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    include_unassigned: PropTypes.bool,
    "crew_ids[]": PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    page: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    per_page: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }).isRequired,
  onChangeFilters: PropTypes.func.isRequired,
  onChangeFiltersBatch: PropTypes.func,
  salesPagination: PropTypes.shape({
    page: PropTypes.number,
    per_page: PropTypes.number
  }),
  expensePagination: PropTypes.shape({
    page: PropTypes.number,
    per_page: PropTypes.number
  })
}

export default function Header({ params, onChangeFilters, onChangeFiltersBatch, salesPagination, expensePagination }) {
  const [visible, setVisible] = React.useState(false)
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const location = useLocation()
  const { isPending } = useActiveGuide()
  const showDateSwitcher = TYPE_WITH_DATESWITCHER?.includes(params?.type)
  const needReload = location?.state?.reload || false
  const salesPaginationRef = React.useRef(salesPagination)
  const expensePaginationRef = React.useRef(expensePagination)
  const paramsRef = React.useRef(params)
  React.useEffect(() => { salesPaginationRef.current = salesPagination }, [salesPagination])
  React.useEffect(() => { expensePaginationRef.current = expensePagination }, [expensePagination])
  React.useEffect(() => { paramsRef.current = params }, [params])

  const changeTypeHandler = useCallback(
    (type) => {
      const hasDateswitcher = TYPE_WITH_DATESWITCHER?.includes(type)
      const date = hasDateswitcher ? dayjs().format(DATE_FORMAT) : null
      const batch = { type, date, search: null, date_gteq: null, date_lteq: null }
      if (type === "business_overview") batch.overview_range = "current_month"
      if (date) {
        batch.range = DEFAULT_RANGE
        const d = dayjs(date, DATE_FORMAT)
        batch.date_gteq = d.startOf("month").format(DATE_FORMAT)
        batch.date_lteq = d.endOf("month").format(DATE_FORMAT)
      } else {
        batch.range = null
      }
      if (type === "tip_report") batch.include_unassigned = true
      if (onChangeFiltersBatch) {
        onChangeFiltersBatch(batch)
      } else {
        Object.entries(batch).forEach(([name, value]) => onChangeFilters({ target: { name, value } }))
      }
    },
    [onChangeFilters, onChangeFiltersBatch]
  )

  useEffect(() => {
    if (showDateSwitcher && !params?.range) onChangeFilters({ target: { value: DEFAULT_RANGE, name: "range" } })
    else if (!showDateSwitcher && params?.range) onChangeFilters({ target: { value: null, name: "range" } })
  }, [params?.range, showDateSwitcher]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (params?.type === "tip_report" && params?.include_unassigned === undefined) {
      onChangeFilters({ target: { value: true, name: "include_unassigned" } })
    }
  }, [params?.type]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = useCallback(() => {
    const p = paramsRef.current
    if (!p?.type) return
    const searchParams = requestParams(p, salesPaginationRef.current, expensePaginationRef.current)
    if (!searchParams) return
    dispatch(getGuideAccounting(searchParams))
  }, [dispatch])

  const fetchOverview = useCallback(() => {
    const p = paramsRef.current
    if (!p?.type) return
    const searchParams = {}
    const overviewRange = p?.overview_range || "current_month"
    if (overviewRange === "total_by_scope") {
      if (p?.date_gteq && p?.date_lteq) searchParams.q = { date_gteq: p.date_gteq, date_lteq: p.date_lteq }
    } else {
      searchParams.date_range_type = overviewRange
    }
    dispatch(getGuideAccountingOverview(searchParams))
  }, [dispatch])

  const getAccountingDataHandler = useCallback(
    async (downloadType) => {
      const exportParams = buildExportParams(params, downloadType)
      const exportUrl = params?.type === "tip_report" ? "/guide/tip_reports/export" : "/guide/accounting/export"
      const filename = buildExportFilename(downloadType, params?.type)
      dispatch(api({ url: exportUrl, params: exportParams })).then((data) => {
        const blob = new Blob([data], { type: "text/csv" })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = url
        link.setAttribute("download", filename)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      })
    },
    [dispatch, params]
  )

  const fetchData = useCallback(() => {
    const p = paramsRef.current
    if (!p?.type) return
    if (p.type === "tip_report") {
      const searchParams = requestParams(p)
      if (searchParams) dispatch(getGuideTipReport(searchParams))
      return
    }
    if (p.type === "business_overview") fetchOverview()
    else fetchAll()
  }, [dispatch, fetchAll, fetchOverview])

  useEffect(() => {
    fetchData()
  }, [
    fetchData,
    params?.type,
    params?.overview_range,
    params?.date,
    params?.range,
    params?.date_gteq,
    params?.date_lteq,
    params?.payment_type,
    params?.booking_type,
    params?.transaction_type,
    params?.experience,
    params?.search,
    params?.category_id,
    params?.["crew_ids[]"],
    params?.include_unassigned,
    params?.page,
    params?.per_page,
  ]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (params?.type === "sales" || params?.type === "expenses") fetchAll()
  }, [fetchAll, salesPagination?.page, salesPagination?.per_page, expensePagination?.page, expensePagination?.per_page]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => dispatch(cleanExpenses()), []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!needReload) return
    window.history.replaceState({}, document.title)
    fetchData()
  }, [needReload]) // eslint-disable-line react-hooks/exhaustive-deps

  const tabItems = useMemo(
    () =>
      Object.keys(TYPE_OPTIONS_ENUM || {}).map((type) => ({
        key: type,
        label: t(`accounting.types.${type}`)
      })),
    [t]
  )

  const downloadMenu = useMemo(
    () => [
      { key: "expenses", label: t("accounting.download_expenses") },
      { key: "sales", label: t("accounting.download_sales") }
    ],
    [t]
  )

  const handleDownloadMenuClick = useCallback(({ key }) => {
    if (key) getAccountingDataHandler(key)
  }, [getAccountingDataHandler])

  const handleOpenModal = useCallback(() => setVisible(true), [])
  const handleCloseModal = useCallback(() => setVisible(false), [])
  const handleDownloadAll = useCallback(() => getAccountingDataHandler(), [getAccountingDataHandler])

  return (
    <Flex vertical gap={5} className={styles.header}>
      <Flex justify="space-between" align="center" wrap gap={18} className={styles.topRow}>
        <BreadCrumbs iconName="expense" category={t("accounting.breadcrumb")} />

        <Flex wrap gap={12} className={styles.actions}>
          {params?.type !== "tip_report" && (
            <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenModal} disabled={isPending} className={styles.addButton}>
              {t("expense.add")}
            </Button>
          )}

          {params?.type === "business_overview" ? (
            <Dropdown menu={{ items: downloadMenu, onClick: handleDownloadMenuClick }} placement="bottomRight" trigger={["click"]}>
              <Button icon={<DownloadOutlined />} className={styles.downloadButton}>
                {t("accounting.download_csv")}
              </Button>
            </Dropdown>
          ) : (
            <Button icon={<DownloadOutlined />} className={styles.downloadButton} onClick={handleDownloadAll}>
              {t("accounting.download_csv")}
            </Button>
          )}
        </Flex>
      </Flex>

      <Flex className={styles.tabsRow}>
        <Tabs
          activeKey={params?.type || DEFAULT_TYPE}
          onChange={changeTypeHandler}
          className={styles.tabs}
          tabBarStyle={{ margin: 0, padding: 0 }}
          type="line"
          items={tabItems}
        />
      </Flex>

      {visible && <FormAntdModal setVisible={setVisible} visible={visible} onClose={handleCloseModal} onSuccess={fetchData} />}
    </Flex>
  )
}
